import type { ViewEndpoint } from "../protocol/events";

/**
 * Whether the user has hold of the screen, told to the scanner.
 *
 * The scanner will not start a detector run under a finger or behind a panel — UI work
 * and inference contend for one main thread and, on GPU backends, one device process —
 * but it cannot see either. This watches the page and says two things: `interaction`,
 * a level, true from the first finger down (or a panel opening) until the last one
 * lifts (and every panel is closed); and `activity`, a pulse for the momentary events
 * that have no duration of their own — a click, a keystroke, a scroll tick. How long to
 * stay off after either is the scanner's policy, not this file's.
 *
 * Pointer capture means the matching release still reaches `document` when a drag
 * leaves its original control, which is why the listeners sit on the document, in the
 * capture phase, rather than on any one element. Panels are watched for their `hidden`
 * attribute, so opening one from anywhere counts without the panel having to say so.
 */
export function mountInteractionMonitor(endpoint: ViewEndpoint, panels: HTMLElement[]): void {
  const pointers = new Set<number>();
  let active = false;

  const openPanels = (): number => panels.filter((panel) => !panel.hidden).length;
  const evaluate = (): void => {
    const next = pointers.size > 0 || openPanels() > 0;
    if (next === active) return;
    active = next;
    endpoint.emit("interaction", { active });
  };

  document.addEventListener(
    "pointerdown",
    (event) => {
      pointers.add(event.pointerId);
      evaluate();
    },
    true,
  );
  const release = (event: PointerEvent): void => {
    pointers.delete(event.pointerId);
    evaluate();
  };
  document.addEventListener("pointerup", release, true);
  document.addEventListener("pointercancel", release, true);

  // A drag can end outside this window without delivering its final pointerup.
  // Match the sheet's focus-loss cleanup, retaining any open modal's pause.
  const releaseAll = (): void => {
    pointers.clear();
    evaluate();
  };
  window.addEventListener("blur", releaseAll);
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) releaseAll();
  });

  const pulses = ["click", "input", "change", "keydown", "scroll", "wheel"] as const;
  for (const type of pulses) {
    document.addEventListener(type, () => endpoint.emit("activity"), true);
  }

  const observer = new MutationObserver(evaluate);
  for (const panel of panels)
    observer.observe(panel, { attributes: true, attributeFilter: ["hidden"] });
}
